import React, { useMemo, useEffect, useState } from 'react';
import {
	Box,
	styled,
	Card,
	CardContent,
	Typography,
	FormControl,
	RadioGroup,
	FormControlLabel,
	Radio,
	Button,
} from '@mui/material';

// 1) Define the Song interface
export interface Song {
	name: string;
	lyrics: string;
}

// 2) Helper function: gather all words from all songs
function getAllWordsFromSongs(songs: Song[]): string[] {
	return songs
		.map((song) => song.lyrics)
		.join(' ')
		.split(/\s+/)
		.filter(Boolean);
}

// 3) The FallingWordsBackground component
//    Takes songs as a prop, collects all words, and animates them falling.
interface FallingWordsProps {
	songs: Song[];
}

const FallingWord = styled(Box)({
	position: 'absolute',
	top: '-5%', // start above visible area
	fontSize: '1rem',
	opacity: 1,
	animation: 'fall linear forwards',
	// Define keyframes in plain CSS:
	'@keyframes fall': {
		'0%': {
			transform: 'translateY(0)',
			opacity: 1,
		},
		'100%': {
			transform: 'translateY(120vh)', // fall below the screen
			opacity: 0,
		},
	},
});

export const FallingWordsBackground: React.FC<FallingWordsProps> = ({ songs }) => {
	const allWords = useMemo(() => getAllWordsFromSongs(songs), [songs]);
	const [fallingWords, setFallingWords] = useState<
		{ id: number; text: string; left: string; duration: number }[]
	>([]);

	useEffect(() => {
		// Spawn a new falling word every 2 seconds
		const interval = setInterval(() => {
			if (allWords.length === 0) return;

			// trim the word so that there is no punctuation
			const randomWord = allWords[Math.floor(Math.random() * allWords.length)];
			const randomLeft = Math.floor(Math.random() * 90); // 0..90%

			setFallingWords((prev) => [
				...prev,
				{
					id: Math.random(),
					text: randomWord,
					left: `${randomLeft}%`,
					duration: 6 + Math.random() * 4, // 6..10s
				},
			]);
		}, 2000);

		return () => clearInterval(interval);
	}, [allWords]);

	return (
		<Box
			sx={{
				position: 'fixed',
				width: '100vw',
				height: '100vh',
				top: 0,
				left: 0,
				zIndex: -1, // behind the main content
				overflow: 'hidden',
				backgroundColor: '#f9f9f9',
			}}
		>
			{fallingWords.map((word) => (
				<FallingWord
					key={word.id}
					sx={{
						left: word.left,
						animationDuration: `${word.duration}s`,
					}}
				>
					{word.text}
				</FallingWord>
			))}
		</Box>
	);
};












// 4) A FunQuestionnaire component using MUI, also taking songs as a prop
interface FunQuestionnaireProps {
	songs: Song[];
}

function getSongTitles(songs: Song[]): string[] {
	return songs.map((s) => s.name);
}

function pickRandom<T>(array: T[]): T {
	return array[Math.floor(Math.random() * array.length)];
}

// For the question: 50% real title, 50% fake
function generateQuestion(songs: Song[]) {
	const allTitles = getSongTitles(songs);
	const isReal = Math.random() < 0.5;

	if (isReal && allTitles.length > 0) {
		const realTitle = pickRandom(allTitles);
		return { text: realTitle, answer: true };
	} else {
		// Create a fake title
		const fakeTitle = 'אהבה לא ידועה ' + Math.floor(Math.random() * 1000);
		return { text: fakeTitle, answer: false };
	}
}

export const FunQuestionnaire: React.FC<FunQuestionnaireProps> = ({ songs }) => {
	const [question, setQuestion] = useState(() => generateQuestion(songs));
	const [userAnswer, setUserAnswer] = useState<boolean | null>(null);
	const [feedback, setFeedback] = useState('');

	const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		setUserAnswer(event.target.value === 'true');
	};

	const handleSubmit = () => {
		if (userAnswer === null) return;
		if (userAnswer === question.answer) {
			setFeedback('Correct!');
		} else {
			setFeedback('Not quite.');
		}
	};

	const handleNext = () => {
		setQuestion(generateQuestion(songs));
		setUserAnswer(null);
		setFeedback('');
	};

	// If there are no songs, handle that gracefully:
	if (songs.length === 0) {
		return (
			<Box sx={{ p: 2 }}>
				<Typography>No songs available yet.</Typography>
			</Box>
		);
	}

	return (
		<Box sx={{ maxWidth: 400, margin: '2rem auto' }}>
			<Card>
				<CardContent>
					<Typography variant="h6">
						Is this an actual Idan Amedi song title?
					</Typography>

					<Typography variant="body1" sx={{ mt: 2, mb: 2 }}>
						<strong>{question.text}</strong>
					</Typography>

					<FormControl component="fieldset">
						<RadioGroup value={userAnswer?.toString() || ''} onChange={handleChange}>
							<FormControlLabel value="true" control={<Radio />} label="Yes" />
							<FormControlLabel value="false" control={<Radio />} label="No" />
						</RadioGroup>
					</FormControl>

					<Button variant="contained" onClick={handleSubmit} sx={{ mt: 2 }}>
						Check
					</Button>

					{feedback && (
						<>
							<Typography variant="subtitle1" sx={{ mt: 2 }}>
								{feedback}
							</Typography>
							<Button variant="outlined" onClick={handleNext} sx={{ mt: 2 }}>
								Next Question
							</Button>
						</>
					)}
				</CardContent>
			</Card>
		</Box>
	);
};
